/**
 * La fiche d'un surplus de chantier (V1.6-10B).
 *
 * Ce que cet écran tient :
 *
 * * **déclarer n'est pas décider, décider n'est pas exécuter.** Trois gestes,
 *   trois moments ; seule l'exécution déplace la marchandise ;
 * * **rien n'est recalculé ici.** Déclaré, disposé, reste et les droits
 *   (`can_open`, `can_decide`, `can_finalize`) viennent du serveur ;
 * * **aucun endroit par défaut.** Un retour au stock demande où ; un transfert
 *   demande où *et* pour quel chantier. HD-Vault ne choisit pas d'entrepôt à
 *   la place de quelqu'un ;
 * * **chaque type ne montre que ses champs.** Un rebut n'a pas de destination,
 *   un retour fournisseur n'a pas d'entrepôt mais une réception d'origine —
 *   afficher les quatre à chaque fois ferait remplir des cases qui ne servent
 *   pas ;
 * * **la réception d'origine se choisit, elle ne se devine pas** (V1.6-10B.1).
 *   L'écran liste les réceptions admissibles avec de quoi les reconnaître et
 *   n'en présélectionne aucune. Prendre la plus récente serait une décision
 *   commerciale prise par personne, qu'il faudrait défaire chez le
 *   fournisseur.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PackageOpen, Play, Trash2, Truck, Undo2, X } from "lucide-react";

import {
  useCancelDisposition,
  useCancelSurplus,
  useCreateDisposition,
  useFinalizeDisposition,
  useOpenSurplus,
  useProjectSurplus,
  useReturnableReceipts,
} from "@/api/project-surpluses";
import { useLocationTree, useProjects } from "@/api/queries";
import type {
  LocationNode,
  ProjectSurplusDetail,
  ReturnableReceipt,
  SurplusDisposition,
  SurplusDispositionType,
  SurplusLine,
  Uuid,
} from "@/api/types";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Dialog } from "@/components/Dialog";
import { Field, Input, Select } from "@/components/Input";
import { ErrorState, TableSkeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDate,
  formatDateTime,
  formatDispositionStatus,
  formatDispositionType,
  formatQuantityWithUnit,
  formatSurplusStatus,
  formatUnit,
} from "@/lib/format";
import { reportMutationError } from "@/lib/mutation-errors";
import { dispositionTone, surplusTone } from "@/lib/stock";

/** Les quatre décisions, dans l'ordre où on les prend le plus souvent. */
const ACTIONS: { type: SurplusDispositionType; label: string; icon: typeof Undo2 }[] = [
  { type: "RETURN_TO_STOCK", label: "Retourner au stock", icon: Undo2 },
  { type: "TRANSFER_TO_PROJECT", label: "Transférer à un projet", icon: Truck },
  { type: "RETURN_TO_SUPPLIER", label: "Retour fournisseur", icon: PackageOpen },
  { type: "SCRAP", label: "Mettre au rebut", icon: Trash2 },
];

function flatten(nodes: LocationNode[], depth = 0): { id: Uuid; label: string }[] {
  return nodes.flatMap((node) => [
    { id: node.id, label: `${"  ".repeat(depth)}${node.name}` },
    ...flatten(node.children ?? [], depth + 1),
  ]);
}

export function ProjectSurplusPage() {
  const { surplusId } = useParams<{ surplusId: string }>();
  const { data, isLoading, error, refetch } = useProjectSurplus(surplusId);

  if (isLoading) {
    return (
      <Page>
        <TableSkeleton rows={6} columns={5} />
      </Page>
    );
  }
  if (error || !data) {
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  }

  const { surplus } = data;
  return (
    <Page>
      <PageHeader
        title={surplus.code}
        subtitle={`${surplus.project_name ?? "Chantier"}${
          surplus.source_location_name ? ` · ${surplus.source_location_name}` : ""
        }`}
        actions={
          <StatusBadge tone={surplusTone(surplus.status)}>
            {formatSurplusStatus(surplus.status)}
          </StatusBadge>
        }
      />

      <div className="grid gap-4 @4xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-4">
          <SurplusLines detail={data} />
          <Dispositions detail={data} />
        </div>
        <div className="space-y-4">
          <Origin detail={data} />
          <Decisions detail={data} />
        </div>
      </div>
    </Page>
  );
}

/* ------------------------------------------------------------- ce qui reste */

function SurplusLines({ detail }: { detail: ProjectSurplusDetail }) {
  const [deciding, setDeciding] = useState<SurplusLine | null>(null);
  const { surplus } = detail;

  const canDecide = (line: SurplusLine) =>
    detail.can_decide && Number(line.remaining) > 0;

  return (
    <Card>
      <CardHeader
        title="Articles déclarés"
        subtitle="Déclaré, disposé et reste sont calculés par le serveur à chaque lecture."
      />
      <div className="hidden md:block">
        <TableShell>
          <thead>
            <tr>
              <Th className="max-w-0">Article</Th>
              <Th align="right" className="whitespace-nowrap">
                Déclaré
              </Th>
              <Th align="right" className="whitespace-nowrap">
                Disposé
              </Th>
              <Th align="right" className="whitespace-nowrap">
                Reste
              </Th>
              <Th className="w-0" />
            </tr>
          </thead>
          <tbody>
            {surplus.lines.map((line) => (
              <Tr key={line.id}>
                <Td className="max-w-0">
                  <span className="block truncate font-medium text-ink">
                    {line.description ?? "Article"}
                  </span>
                  {line.note && (
                    <span className="block truncate text-[11px] text-ink-subtle">
                      {line.note}
                    </span>
                  )}
                </Td>
                <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink">
                  {formatQuantityWithUnit(line.quantity_declared, line.unit)}
                </Td>
                <Td align="right" className="tnum whitespace-nowrap text-[13px] text-ink-muted">
                  {formatQuantityWithUnit(line.disposed, line.unit)}
                </Td>
                <Td
                  align="right"
                  className={`tnum whitespace-nowrap text-[13px] ${
                    Number(line.remaining) > 0 ? "text-warning" : "text-ink-muted"
                  }`}
                >
                  {formatQuantityWithUnit(line.remaining, line.unit)}
                </Td>
                <Td className="w-0 whitespace-nowrap">
                  {canDecide(line) && (
                    <Button variant="ghost" size="sm" onClick={() => setDeciding(line)}>
                      Décider
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        </TableShell>
      </div>

      {/* À 390 px, une carte par article : un tableau de cinq colonnes réduit
          n'est pas un écran de terrain. */}
      <ul className="divide-y divide-border/60 md:hidden">
        {surplus.lines.map((line) => (
          <li key={line.id} className="px-3 py-3">
            <p className="text-[14px] font-medium text-ink">{line.description ?? "Article"}</p>
            <dl className="tnum mt-1 grid grid-cols-3 gap-1 text-[12px]">
              <div>
                <dt className="text-ink-subtle">Déclaré</dt>
                <dd className="text-ink">
                  {formatQuantityWithUnit(line.quantity_declared, line.unit)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-subtle">Disposé</dt>
                <dd className="text-ink-muted">
                  {formatQuantityWithUnit(line.disposed, line.unit)}
                </dd>
              </div>
              <div>
                <dt className="text-ink-subtle">Reste</dt>
                <dd className={Number(line.remaining) > 0 ? "text-warning" : "text-ink"}>
                  {formatQuantityWithUnit(line.remaining, line.unit)}
                </dd>
              </div>
            </dl>
            <p className="mt-1 text-[11px] text-ink-subtle">
              Source : {surplus.source_location_name ?? "—"}
            </p>
            {canDecide(line) && (
              <Button
                variant="secondary"
                size="sm"
                fullWidth
                className="mt-2"
                onClick={() => setDeciding(line)}
              >
                Décider
              </Button>
            )}
          </li>
        ))}
      </ul>

      {deciding && (
        <DecideDialog
          surplusId={surplus.id}
          line={deciding}
          sourceName={surplus.source_location_name}
          onClose={() => setDeciding(null)}
        />
      )}
    </Card>
  );
}

/* --------------------------------------------------------------- décisions */

function Dispositions({ detail }: { detail: ProjectSurplusDetail }) {
  const toast = useToast();
  const finalize = useFinalizeDisposition();
  const cancel = useCancelDisposition();
  const { surplus } = detail;

  const rows = useMemo(
    () =>
      surplus.lines.flatMap((line) =>
        line.dispositions.map((item) => ({ line, item })),
      ),
    [surplus.lines],
  );

  if (rows.length === 0) return null;

  const run = (item: SurplusDisposition) =>
    finalize.mutate(
      { surplusId: surplus.id, dispositionId: item.id },
      {
        onSuccess: () =>
          toast.success(
            "Décision exécutée",
            "La marchandise a bougé. Le mouvement est écrit au grand livre.",
          ),
        onError: (error) => reportMutationError(toast, "Exécution refusée", error),
      },
    );

  const drop = (item: SurplusDisposition) =>
    cancel.mutate(
      { surplusId: surplus.id, dispositionId: item.id },
      {
        onSuccess: () => toast.success("Décision annulée", "Rien n'avait bougé."),
        onError: (error) => reportMutationError(toast, "Annulation refusée", error),
      },
    );

  return (
    <Card>
      <CardHeader
        title="Décisions"
        subtitle="Une décision à exécuter n'a rien déplacé. Exécuter écrit le mouvement, une fois."
      />
      <ul className="divide-y divide-border/60">
        {rows.map(({ line, item }) => (
          <li key={item.id} className="px-3 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-[13px] font-medium text-ink">
                  {formatDispositionType(item.disposition_type)}
                </p>
                <p className="tnum text-[12px] text-ink-muted">
                  {formatQuantityWithUnit(item.quantity, item.unit)} ·{" "}
                  {line.description ?? "Article"}
                </p>
              </div>
              <StatusBadge tone={dispositionTone(item.status)}>
                {formatDispositionStatus(item.status)}
              </StatusBadge>
            </div>

            <p className="mt-1 text-[11px] text-ink-subtle">
              {surplus.source_location_name ?? "—"} →{" "}
              {item.disposition_type === "SCRAP"
                ? "rebut"
                : item.disposition_type === "RETURN_TO_SUPPLIER"
                  ? "fournisseur"
                  : "destination choisie"}
              {item.reason ? ` · ${item.reason}` : ""}
              {item.finalized_at ? ` · ${formatDateTime(item.finalized_at)}` : ""}
            </p>

            {item.status === "FINALIZED" && item.supplier_return_id && (
              <p className="mt-1 text-[11px] text-ink-subtle">
                Le mouvement appartient au{" "}
                <Link
                  to={`/retours-fournisseur/${item.supplier_return_id}`}
                  className="text-brand hover:underline"
                >
                  retour fournisseur
                </Link>
                .
              </p>
            )}

            {item.status === "DRAFT" && (
              <div className="mt-2 flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!detail.can_finalize || finalize.isPending}
                  onClick={() => run(item)}
                >
                  <Play className="size-4" aria-hidden />
                  Exécuter
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={cancel.isPending}
                  onClick={() => drop(item)}
                >
                  <X className="size-4" aria-hidden />
                  Annuler
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

/* ---------------------------------------------------------------- décider */

function DecideDialog({
  surplusId,
  line,
  sourceName,
  onClose,
}: {
  surplusId: Uuid;
  line: SurplusLine;
  sourceName: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateDisposition();
  const { data: locations } = useLocationTree();
  const { data: projects } = useProjects({ limit: 200 });
  const [type, setType] = useState<SurplusDispositionType>("RETURN_TO_STOCK");
  const [quantity, setQuantity] = useState(line.remaining);
  const [destination, setDestination] = useState("");
  const [project, setProject] = useState("");
  const [source, setSource] = useState<ReturnableReceipt | null>(null);
  const [reason, setReason] = useState("");

  const places = useMemo(() => flatten(locations ?? []), [locations]);
  const needsDestination = type === "RETURN_TO_STOCK" || type === "TRANSFER_TO_PROJECT";
  const needsProject = type === "TRANSFER_TO_PROJECT";
  const isSupplierReturn = type === "RETURN_TO_SUPPLIER";

  // La liste n'est demandée que si quelqu'un a choisi « retour fournisseur ».
  const { data: receipts, isLoading: loadingReceipts } = useReturnableReceipts(
    surplusId,
    line.id,
    isSupplierReturn,
  );

  /**
   * Un retour fournisseur se saisit **dans l'unité de la réception choisie**.
   *
   * C'est elle qui borne — six mètres reçus bornent à six mètres — et saisir
   * dans une autre unité ferait comparer deux chiffres qui ne parlent pas de
   * la même chose. C'était le défaut de V1.6-10A.
   */
  const unit = isSupplierReturn && source ? source.unit : line.unit;
  const overSource =
    isSupplierReturn && source !== null && Number(quantity) > Number(source.returnable);

  const ready =
    Number(quantity) > 0 &&
    (!needsDestination || Boolean(destination)) &&
    (!needsProject || Boolean(project)) &&
    (!isSupplierReturn || (source !== null && !overSource));

  const submit = () =>
    create.mutate(
      {
        surplusId,
        line_id: line.id,
        disposition_type: type,
        quantity,
        unit: isSupplierReturn && source ? source.unit : null,
        destination_location_id: needsDestination ? destination : null,
        destination_project_id: needsProject ? project : null,
        receipt_line_id: isSupplierReturn && source ? source.receipt_line_id : null,
        supplier_id: isSupplierReturn && source ? source.supplier_id : null,
        reason: reason.trim() || null,
      },
      {
        onSuccess: () => {
          toast.success(
            "Décision enregistrée",
            "Rien n'a encore bougé. Exécutez-la quand la marchandise part.",
          );
          onClose();
        },
        onError: (error) => reportMutationError(toast, "Décision refusée", error),
      },
    );

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Que faire de ce surplus ?"
      description={`${line.description ?? "Article"} — reste ${formatQuantityWithUnit(
        line.remaining,
        line.unit,
      )}${sourceName ? ` à ${sourceName}` : ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={submit} disabled={!ready || create.isPending} loading={create.isPending}>
            Enregistrer la décision
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-[12px] text-ink-muted">
          Enregistrer une décision ne déplace rien. La marchandise bouge à l&apos;exécution,
          et seulement si elle est réellement là.
        </p>

        {/* Quatre cibles plutôt qu'un menu déroulant : cet écran se manipule
            avec des gants, et la décision est le geste principal. */}
        <fieldset>
          <legend className="mb-1.5 text-[13px] font-medium text-ink-muted">Décision</legend>
          <div className="grid grid-cols-2 gap-2">
            {ACTIONS.map((action) => {
              const Icon = action.icon;
              const chosen = type === action.type;
              return (
                <button
                  key={action.type}
                  type="button"
                  aria-pressed={chosen}
                  onClick={() => {
                    setType(action.type);
                    // Changer de geste efface la source : une réception
                    // choisie pour un retour n'a aucun sens pour un rebut.
                    setSource(null);
                  }}
                  className={`flex h-16 flex-col items-center justify-center gap-1 rounded-md border px-2 text-center text-[12px] transition-colors ${
                    chosen
                      ? "border-brand/50 bg-brand-subtle text-brand"
                      : "border-border text-ink-muted hover:border-border-strong hover:text-ink"
                  }`}
                >
                  <Icon className="size-4" aria-hidden />
                  {action.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <Field
          label={`Quantité (${formatUnit(unit)})`}
          htmlFor="disposition-quantity"
          hint={
            isSupplierReturn && source
              ? `Cette réception permet encore ${formatQuantityWithUnit(
                  source.returnable,
                  source.unit,
                )}.`
              : undefined
          }
          error={
            overSource
              ? "Cette réception ne permet plus d'en renvoyer autant. Choisissez une autre réception pour le reste."
              : undefined
          }
        >
          <Input
            id="disposition-quantity"
            inputMode="decimal"
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </Field>

        {/* Chaque type ne montre que ses champs : afficher les quatre à chaque
            fois ferait remplir des cases qui ne servent pas. */}
        {needsDestination && (
          <Field label="Où ça va" htmlFor="disposition-destination">
            <Select
              id="disposition-destination"
              value={destination}
              onChange={(event) => setDestination(event.target.value)}
            >
              <option value="">Choisir un endroit…</option>
              {places.map((place) => (
                <option key={place.id} value={place.id}>
                  {place.label}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {needsProject && (
          <Field label="Pour quel chantier" htmlFor="disposition-project">
            <Select
              id="disposition-project"
              value={project}
              onChange={(event) => setProject(event.target.value)}
            >
              <option value="">Choisir un chantier…</option>
              {(projects?.items ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.code} — {item.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {isSupplierReturn && (
          <ReceiptChoice
            receipts={receipts}
            loading={loadingReceipts}
            chosen={source}
            onChoose={(receipt) => {
              setSource(receipt);
              // La quantité repart à vide : elle se saisit dans l'unité de la
              // réception, et garder « 1000 » d'une déclaration en centimètres
              // ferait lire mille mètres. Un champ vide ne suggère rien.
              setQuantity("");
            }}
          />
        )}

        <Field label="Motif (facultatif)" htmlFor="disposition-reason">
          <Input
            id="disposition-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * D'où la marchandise repart (V1.6-10B.1).
 *
 * **Rien n'est présélectionné, et rien n'est marqué « la bonne ».** Une même
 * référence a pu arriver quatre fois, de deux fournisseurs, à trois prix ;
 * prendre la plus récente serait une décision commerciale prise par personne,
 * qu'il faudrait ensuite défaire chez le fournisseur. Chaque option porte de
 * quoi la reconnaître : le fournisseur, le bon, la réception, sa date, et ce
 * qu'elle permet encore de renvoyer.
 *
 * Une liste vide est une réponse, pas une erreur : il n'y a pas de réception
 * admissible, donc pas de retour fournisseur possible. Le dire vaut mieux que
 * de laisser quelqu'un remplir un formulaire qui sera refusé.
 */
function ReceiptChoice({
  receipts,
  loading,
  chosen,
  onChoose,
}: {
  receipts: ReturnableReceipt[] | undefined;
  loading: boolean;
  chosen: ReturnableReceipt | null;
  onChoose: (receipt: ReturnableReceipt) => void;
}) {
  if (loading) {
    return (
      <p className="text-[12px] text-ink-subtle" role="status">
        Recherche des réceptions…
      </p>
    );
  }

  if (!receipts || receipts.length === 0) {
    return (
      <p className="rounded-md border border-warning/30 bg-warning-subtle px-3 py-2 text-[12px] text-ink">
        Aucune réception admissible trouvée pour ce produit. Un retour fournisseur part de
        la réception qui a fait entrer la marchandise, et il n&apos;y en a aucune qui
        permette encore d&apos;en renvoyer.
      </p>
    );
  }

  return (
    <fieldset>
      <legend className="mb-1.5 text-[13px] font-medium text-ink-muted">
        De quelle réception vient-elle ?
      </legend>
      <p className="mb-2 text-[12px] text-ink-subtle">
        HD-Vault ne devine pas la réception et ne prend pas la plus récente. Choisissez
        celle que vous renvoyez.
      </p>
      <ul className="flex flex-col gap-2">
        {receipts.map((receipt) => {
          const selected = chosen?.receipt_line_id === receipt.receipt_line_id;
          return (
            <li key={receipt.receipt_line_id}>
              <button
                type="button"
                aria-pressed={selected}
                onClick={() => onChoose(receipt)}
                className={`w-full rounded-md border px-3 py-2 text-left transition-colors ${
                  selected
                    ? "border-brand/50 bg-brand-subtle"
                    : "border-border hover:border-border-strong"
                }`}
              >
                <span className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
                  <span className="text-[13px] font-medium text-ink">
                    {receipt.supplier_name}
                  </span>
                  <span className="tnum text-[13px] text-ink">
                    {formatQuantityWithUnit(receipt.returnable, receipt.unit)} renvoyables
                  </span>
                </span>
                <span className="mt-0.5 block font-mono text-[11px] text-ink-subtle">
                  {receipt.purchase_order_code} · {receipt.receipt_code} ·{" "}
                  {formatDate(receipt.received_at)}
                </span>
                <span className="tnum mt-0.5 block text-[11px] text-ink-subtle">
                  Reçu {formatQuantityWithUnit(receipt.received, receipt.unit)} · déjà
                  renvoyé {formatQuantityWithUnit(receipt.already_returned, receipt.unit)}
                </span>
                {(receipt.description || receipt.mpn) && (
                  <span className="mt-0.5 block truncate text-[11px] text-ink-subtle">
                    {receipt.description}
                    {receipt.mpn ? ` · ${receipt.mpn}` : ""}
                  </span>
                )}
                {receipt.matches_surplus_project && (
                  <span className="mt-1 inline-block rounded-full border border-border px-2 py-0.5 text-[10px] text-ink-muted">
                    Bon rattaché à ce chantier
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}


/* ------------------------------------------------------------- la colonne */

function Origin({ detail }: { detail: ProjectSurplusDetail }) {
  const { surplus } = detail;
  return (
    <Card>
      <CardHeader title="Provenance" />
      <CardBody>
        <dl className="space-y-2 text-[13px]">
          <div>
            <dt className="text-ink-subtle">Chantier</dt>
            <dd className="text-ink">
              <Link to={`/projects/${surplus.project_id}`} className="hover:text-brand">
                {surplus.project_name ?? "Chantier"}
              </Link>
              {surplus.project_code && (
                <span className="block font-mono text-[11px] text-ink-subtle">
                  {surplus.project_code}
                </span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-ink-subtle">Source</dt>
            <dd className="text-ink">{surplus.source_location_name ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-ink-subtle">Déclaré par</dt>
            <dd className="text-ink">
              {surplus.declared_by}
              {surplus.declared_at ? ` · ${formatDateTime(surplus.declared_at)}` : ""}
            </dd>
          </div>
          {surplus.note && (
            <div>
              <dt className="text-ink-subtle">Note</dt>
              <dd className="text-ink">{surplus.note}</dd>
            </div>
          )}
        </dl>
      </CardBody>
    </Card>
  );
}

function Decisions({ detail }: { detail: ProjectSurplusDetail }) {
  const toast = useToast();
  const open = useOpenSurplus();
  const cancel = useCancelSurplus();
  const { surplus } = detail;

  return (
    <Card>
      <CardHeader title="Déclaration" />
      <CardBody className="space-y-3">
        {surplus.stored_status === "DRAFT" && (
          <>
            <p className="text-[13px] text-ink-muted">
              Un brouillon se corrige. Confirmer fige les lignes et ouvre les décisions —
              toujours sans rien déplacer.
            </p>
            <Button
              variant="primary"
              fullWidth
              disabled={!detail.can_open || open.isPending}
              loading={open.isPending}
              onClick={() =>
                open.mutate(
                  { surplusId: surplus.id },
                  {
                    onSuccess: () =>
                      toast.success(
                        "Déclaration confirmée",
                        "Aucun stock n'a bougé. Les décisions sont maintenant possibles.",
                      ),
                    onError: (error) =>
                      reportMutationError(toast, "Confirmation refusée", error),
                  },
                )
              }
            >
              Confirmer la déclaration
            </Button>
          </>
        )}

        {surplus.stored_status === "OPEN" && (
          <p className="text-[13px] text-ink-muted">
            Les lignes sont figées : ce sont elles que chaque décision lit.
          </p>
        )}

        {surplus.stored_status === "CANCELLED" && (
          <p className="text-[13px] text-ink-muted">
            Déclaration abandonnée. Aucune marchandise n&apos;a bougé de son fait.
          </p>
        )}

        {detail.can_cancel && (
          <Button
            variant="ghost"
            fullWidth
            disabled={cancel.isPending}
            onClick={() =>
              cancel.mutate(
                { surplusId: surplus.id },
                {
                  onSuccess: () =>
                    toast.success("Déclaration annulée", "Rien n'avait été exécuté."),
                  onError: (error) => reportMutationError(toast, "Annulation refusée", error),
                },
              )
            }
          >
            Annuler la déclaration
          </Button>
        )}

        {!detail.can_cancel && surplus.stored_status === "OPEN" && (
          <p className="text-[12px] text-ink-subtle">
            Cette déclaration a produit des mouvements : l&apos;annuler effacerait une
            histoire vraie. Corrigez par un nouveau geste.
          </p>
        )}
      </CardBody>
    </Card>
  );
}
